import { useMemo } from "react";
import { useSearchParams } from "react-router-dom";

/**
 * Segmented quick-select for dashboard date ranges. Updates the URL's
 * ?from= and ?to= search params; useDashboardData picks up the change
 * and refetches automatically.
 *
 * Default (28 days) means no params — clean URLs when looking at the
 * common case.
 */
const OPTIONS = [
  { key: "28d", label: "28 days", days: 28, isDefault: true },
  { key: "7d",  label: "7 days",  days: 7 },
  { key: "90d", label: "90 days", days: 90 },
];

function toISODate(d) {
  return d.toISOString().slice(0, 10);
}

function daysAgo(n) {
  const d = new Date();
  d.setUTCDate(d.getUTCDate() - n);
  return toISODate(d);
}

export function DateRangePicker() {
  const [searchParams, setSearchParams] = useSearchParams();
  const fromParam = searchParams.get("from");
  const toParam = searchParams.get("to");

  // Infer the active option by matching the current `from` to a preset.
  const active = useMemo(() => {
    if (!fromParam && !toParam) return "28d";
    for (const opt of OPTIONS) {
      if (opt.isDefault) continue;
      if (fromParam === daysAgo(opt.days)) return opt.key;
    }
    return "custom";
  }, [fromParam, toParam]);

  function select(opt) {
    const next = new URLSearchParams(searchParams);
    if (opt.isDefault) {
      next.delete("from");
      next.delete("to");
    } else {
      next.set("from", daysAgo(opt.days));
      next.set("to", toISODate(new Date()));
    }
    setSearchParams(next, { replace: true });
  }

  return (
    <div
      className="inline-flex items-center gap-0.5 p-0.5 rounded border border-border-default bg-app-bg"
      role="group"
      aria-label="Date range"
    >
      {OPTIONS.map((opt) => {
        const isActive = active === opt.key;
        return (
          <button
            key={opt.key}
            type="button"
            onClick={() => select(opt)}
            className={`px-3 h-7 text-[11px] rounded transition-colors ${
              isActive
                ? "bg-surface text-text-primary font-semibold shadow-sm"
                : "text-text-secondary hover:text-text-primary"
            }`}
          >
            {opt.label}
          </button>
        );
      })}
    </div>
  );
}
